import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Header } from '@/components/layout/Header';
import { StepProgress } from '@/components/onboarding/StepProgress';
import { Step1SubjectGrade } from '@/components/onboarding/Step1SubjectGrade';
import { Step2GoalType } from '@/components/onboarding/Step2GoalType';
import { Step3Preferences } from '@/components/onboarding/Step3Preferences';
import { useUpsertGoalProfile } from '@/hooks/useGoalProfile';
import { consumePendingIntent } from '@/utils/pendingIntent';
import { useSwitchRole } from '@/hooks/use-switch-role';
import { useCurrentUser } from '@/hooks/use-current-user';
import { usePageTitle } from '@/hooks/usePageTitle';

const TOTAL_STEPS = 3;

interface WizardData {
  subject: string;
  gradeBand: string;
  curriculum: string;
  goalType: string;
}

// Read pre-fill context from localStorage (set before login by search page)
function readSearchContext() {
  try {
    const raw = localStorage.getItem('bmc:anonymous-search-context');
    if (!raw) return {};
    const ctx = JSON.parse(raw) as {
      activeFilters?: { gradeBand?: string; curriculum?: string[] };
    };
    return {
      gradeBand: ctx.activeFilters?.gradeBand ?? '',
      curriculum: ctx.activeFilters?.curriculum?.[0] ?? '',
    };
  } catch {
    return {};
  }
}

export default function GoalOnboardingPage() {
  usePageTitle('Set Your Learning Goal | BookMyCoaching');
  const navigate = useNavigate();
  const { ensureRole } = useSwitchRole();
  const { user: currentUser } = useCurrentUser();
  const { mutateAsync: saveGoalProfile } = useUpsertGoalProfile();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const searchCtx = readSearchContext();

  const [wizardData, setWizardData] = useState<WizardData>({
    subject: '',
    gradeBand: searchCtx.gradeBand ?? '',
    curriculum: searchCtx.curriculum ?? '',
    goalType: '',
  });

  const handleStep1 = (data: { subject: string; gradeBand: string; curriculum: string }) => {
    setWizardData(prev => ({ ...prev, ...data }));
    setStep(2);
  };

  const handleStep2 = (goalType: string) => {
    setWizardData(prev => ({ ...prev, goalType }));
    setStep(3);
  };

  const handleStep3 = async (data: {
    budgetMin: number | undefined;
    budgetMax: number | undefined;
    currency: string;
    modePreference: string;
    startTimeline: string;
  }) => {
    setIsSubmitting(true);
    try {
      // Create the STUDENT role now that the user is committing their first
      // required info. If they abandoned before reaching this step no account
      // was created. Idempotent for users who already have the role.
      //
      // Capture "first role ever" BEFORE calling ensureRole — it triggers an
      // internal refetch() on success, so checking currentUser.roles after
      // the call would always show STUDENT already present. Only a genuine
      // first-ever registration gets the coin bonus welcome toast.
      const wasFirstRoleEver = (currentUser?.roles?.length ?? 0) === 0;
      const ready = await ensureRole('STUDENT');
      if (!ready) {
        setIsSubmitting(false);
        return;
      }
      if (wasFirstRoleEver) {
        sessionStorage.setItem('student_coin_bonus_welcome', 'true');
      }
      await saveGoalProfile({
        subject: wizardData.subject || undefined,
        gradeBand: wizardData.gradeBand || undefined,
        curriculum: wizardData.curriculum || undefined,
        goalType: wizardData.goalType || undefined,
        budgetMin: data.budgetMin,
        budgetMax: data.budgetMax,
        // SCRUM-365: never send a currency; the backend derives it.
        modePreference: data.modePreference || undefined,
        startTimeline: data.startTimeline || undefined,
      });

      const intent = consumePendingIntent();
      if (intent?.type === 'INQUIRY' && intent.teacherHandle) {
        navigate(`/t/${intent.teacherHandle}`, {
          replace: true,
          state: { openInquiry: true },
        });
      } else {
        // Carry the goal criteria into the search URL so results are filtered
        // by what the student just told us — a bare /search renders the blank
        // "no search yet" state instead of running a query.
        const searchParams = new URLSearchParams();
        if (wizardData.subject) searchParams.set('q', wizardData.subject);
        if (wizardData.gradeBand) searchParams.append('gradeBand', wizardData.gradeBand);
        if (wizardData.curriculum) searchParams.append('curriculum', wizardData.curriculum);
        navigate(`/search?${searchParams.toString()}`, { replace: true });
      }
    } catch {
      toast.error('Could not save your preferences. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine CTA label for step 3
  let pendingTeacherName: string | undefined;
  try {
    const raw = localStorage.getItem('bmc:pending-intent');
    if (raw) {
      const intent = JSON.parse(raw);
      if (intent?.teacherHandle) {
        pendingTeacherName = intent.teacherHandle;
      }
    }
  } catch { /* ignore */ }

  const stepTitles = [
    'Tell us about your goals',
    'What are you aiming for?',
    'Your preferences',
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header hideDashboard />

      {/* Page background */}
      <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#ede9fe]/60 via-[#ddd6fe]/40 to-[#e0e7ff]/60" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-[100px] bg-violet-300/30" />
        <div className="absolute bottom-0 -left-32 w-[400px] h-[400px] rounded-full blur-[120px] bg-indigo-300/20" />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10 pt-28">
        <div className="w-full max-w-2xl space-y-4">

          {/* Back link */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Go back
          </button>

          {/* Card */}
          <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white dark:border-gray-700 shadow-xl shadow-slate-200/60 dark:shadow-slate-900/60 overflow-hidden">

            {/* Gradient header band */}
            <div className="bg-gradient-to-r from-[#3d2d6b] to-[#3a4080] px-8 py-7">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-white/50 mb-0.5">
                    Step {step} of {TOTAL_STEPS}
                  </p>
                  <h1 className="text-xl font-bold text-white leading-tight">
                    {stepTitles[step - 1]}
                  </h1>
                  <p className="text-sm text-white/60 mt-1">
                    Helps us match you with the right teacher · 30 seconds
                  </p>
                </div>
              </div>
              <StepProgress currentStep={step} totalSteps={TOTAL_STEPS} inverted />
            </div>

            {/* Step content */}
            <div className="px-8 py-7 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200">
              {step === 1 && (
                <Step1SubjectGrade
                  initialSubject={wizardData.subject}
                  initialGradeBand={wizardData.gradeBand}
                  initialCurriculum={wizardData.curriculum}
                  onNext={handleStep1}
                />
              )}
              {step === 2 && (
                <Step2GoalType
                  gradeBand={wizardData.gradeBand}
                  initialGoalType={wizardData.goalType}
                  onNext={handleStep2}
                  onBack={() => setStep(1)}
                />
              )}
              {step === 3 && (
                <Step3Preferences
                  pendingTeacherName={pendingTeacherName}
                  onFinish={handleStep3}
                  onBack={() => setStep(2)}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>

          </div>

          <p className="text-center text-xs text-slate-400">
            You can update these preferences anytime from your profile settings.
          </p>

        </div>
      </div>
    </div>
  );
}
